import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { X } from "lucide-react"; // Import X icon

// Prayer-focused verses
const prayerVerses = [
  {
    reference: "Philippians 4:6",
    text: "Do not be anxious about anything, but in everything by prayer and supplication with thanksgiving let your requests be made known to God."
  },
  {
    reference: "James 5:16",
    text: "Therefore, confess your sins to one another and pray for one another, that you may be healed. The prayer of a righteous person has great power as it is working."
  },
  {
    reference: "1 Thessalonians 5:17",
    text: "Pray without ceasing."
  },
  {
    reference: "Matthew 6:9-13",
    text: "Pray then like this: 'Our Father in heaven, hallowed be your name. Your kingdom come, your will be done, on earth as it is in heaven...'"
  },
  {
    reference: "Romans 8:26",
    text: "Likewise the Spirit helps us in our weakness. For we do not know what to pray for as we ought, but the Spirit himself intercedes for us with groanings too deep for words."
  },
  {
    reference: "Jeremiah 29:12",
    text: "Then you will call upon me and come and pray to me, and I will hear you."
  },
  {
    reference: "Psalm 145:18",
    text: "The Lord is near to all who call on him, to all who call on him in truth."
  },
  {
    reference: "1 John 5:14",
    text: "And this is the confidence that we have toward him, that if we ask anything according to his will he hears us."
  },
  {
    reference: "Ephesians 6:18",
    text: "Praying at all times in the Spirit, with all prayer and supplication. To that end, keep alert with all perseverance, making supplication for all the saints."
  },
  {
    reference: "Mark 11:24",
    text: "Therefore I tell you, whatever you ask in prayer, believe that you have received it, and it will be yours."
  }
];

interface PrayerHeavenlyLoaderProps {
  message?: string;
  onClose?: () => void;  // Add optional onClose prop
}

const PrayerHeavenlyLoader: React.FC<PrayerHeavenlyLoaderProps> = ({ 
  message = "Preparing our hearts to pray and worship God...", 
  onClose 
}) => {
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [isVerseVisible, setIsVerseVisible] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Get a random verse initially and then change it every 4 seconds
  useEffect(() => {
    const initialIndex = Math.floor(Math.random() * prayerVerses.length);
    setCurrentVerseIndex(initialIndex);
    
    // Change verse every 4 seconds with fade transition
    const verseInterval = setInterval(() => {
      setIsVerseVisible(false);
      
      // After fade out, change the verse
      setTimeout(() => {
        setCurrentVerseIndex(prev => {
          const nextIndex = Math.floor(Math.random() * prayerVerses.length);
          // Ensure we don't get the same verse twice in a row
          return nextIndex === prev ? (nextIndex + 1) % prayerVerses.length : nextIndex;
        });
        setIsVerseVisible(true);
      }, 500); // 500ms for fade out
      
    }, 4000); // 4 seconds between verse changes
    
    return () => clearInterval(verseInterval);
  }, []);

  return (
    <Dialog open={true}>
      <DialogContent className="sm:max-w-md md:max-w-lg bg-heaven-white border-selah-gold/30 p-0 overflow-hidden">
        {/* Add close button */}
        {onClose && (
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-50 text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        )}
        
        <div className="relative flex flex-col items-center justify-center p-6">
          {/* Heavenly glow effect */}
          <div className="absolute inset-0 bg-gradient-radial from-selah-gold/30 via-selah-gold/10 to-transparent animate-aurora"></div>
          
          {/* Prayer hands animation */}
          <div className="relative z-10 mb-6">
            <div className="absolute -inset-4 rounded-full bg-selah-gold/20 blur-md animate-pulse"></div>
            {!imageLoaded && <Skeleton className="w-24 h-24 rounded-full" />}
            <motion.img 
              src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//Corner%20logo%20(8).png"
              alt="Prayer Logo"
              className="relative w-24 h-24 object-contain"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 2, 0, -2, 0]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 3, 
                ease: "easeInOut" 
              }}
              onLoad={() => setImageLoaded(true)}
            />
          </div>

          {/* Prayer verse display */}
          <div className="max-w-md px-4 text-center relative z-10">
            <AnimatePresence mode="wait">
              {isVerseVisible && (
                <motion.div
                  key={currentVerseIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-3"
                >
                  <h2 className="text-xl md:text-2xl font-playfair text-selah-gold">{prayerVerses[currentVerseIndex].reference}</h2>
                  <p className="text-base md:text-lg text-selah-navy font-poppins italic">"{prayerVerses[currentVerseIndex].text}"</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Loading message */}
          <div className="mt-6 text-center relative z-10">
            <motion.p 
              className="text-selah-navy/80 font-poppins"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            >
              {message}
            </motion.p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrayerHeavenlyLoader;
